import MUIButton from '@/components/MUI-Components/MUI-Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, {memo} from 'react';
import dynamic from 'next/dynamic';
import DisplayImage from '@/components/Ui-components/display-image';
import {fadeInUp} from '@/animation/scroll-animation';
import {AnimatedComponent} from '../../AnimatedComponent';
import parse from 'html-react-parser';
import {borderRadius, colors, fontWeight, shadows, zIndex} from '@/styles/tokens';
import {sectionHeadingSx} from '@/styles/sectionHeading';

const ArrowForwardIcon = dynamic(() => import('@mui/icons-material/ArrowForward'), {ssr: false});
const CheckCircleOutlineOutlinedIcon = dynamic(() => import('@mui/icons-material/CheckCircleOutlineOutlined'), {ssr: false});

const projectNameSx = {bgcolor: '#EAEDF3', display: 'inline-block'};
const keyPointSx = {fontWeight: fontWeight.extrabold};
const buttonSx = {textTransform: 'none'};
const shadowSx = {boxShadow: '0 0 16px 5px rgba(0, 0, 0, 0.15)'};

// `::before` pseudo-element + a conditional `right:0` variant for alternating
// (odd-index) cards — both exclusive to this component, so this replaces
// `.client-project-card-wrapper`/`.client-project-card-bg` entirely.
const clientProjectCardSx = (isReversed: boolean) => ({
    position: 'relative',
    '& > *': {
        position: 'relative',
        zIndex: zIndex.raised
    },
    '&::before': {
        position: 'absolute',
        margin: 'auto',
        top: 0,
        bottom: 0,
        content: '""',
        width: '70%',
        backgroundColor: colors.background.default,
        borderRadius: borderRadius['2xl'],
        zIndex: zIndex.base,
        boxShadow: shadows.ring,
        ...(isReversed ? {right: 0} : {})
    },
    '@media (max-width: 992px)': {
        '&::before': {
            width: '100%'
        }
    }
});

const clientProjectDurationSx = {
    '& span': {
        fontWeight: `${fontWeight.bold} !important`
    }
};

const clientProjectImageSx = {
    width: '100%',
    height: 'auto',
    display: 'block'
};

const ClientProject = (props: any) => {
    const {heading, openForm, contentData} = props;
    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    <Typography variant="h1" component="h2" mb={3} className="font-headers" sx={sectionHeadingSx}>
                        {heading}
                    </Typography>
                </Box>
                <Stack spacing={4}>
                    {contentData?.map((item: any, index: number) => (
                        <Box key={index + 1} sx={clientProjectCardSx(index % 2 === 1)} pt={5} pb={{md: 5, xs: 2}}>
                            <AnimatedComponent variants={fadeInUp} delay={index * 0.2}>
                                <Grid container direction={index % 2 === 1 ? 'row-reverse' : 'row'} alignItems="center" spacing={3}>
                                    <Grid item md={6} display="flex" alignItems="center">
                                        <Box px={{sm: 5, xs: 2}}>
                                            <Typography
                                                fontWeight="bold"
                                                component="h3"
                                                fontSize={{xs: 26, sm: 32}}
                                                className="font-headers"
                                            >
                                                {item.heading}
                                            </Typography>
                                            <Typography
                                                sx={projectNameSx}
                                                fontWeight="bold"
                                                px={3}
                                                py={1}
                                                borderRadius={2}
                                                my={2}
                                                fontSize={18}
                                                className="font-headers"
                                            >
                                                {item.projectName}
                                            </Typography>
                                            {item.projectDuration && (
                                                <Box mb={2} sx={clientProjectDurationSx}>
                                                    {item.projectDuration?.map((project: any, index: number) => (
                                                        <Typography key={index} className="font-regular-text" mb={1}>
                                                            {project ? parse(project) : ''}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            )}

                                            {item?.button && (
                                                <Box className="dynamic-screen-button-wrapper">
                                                    {item?.button?.map((buttonItem: any, index: number) => (
                                                        <Box className="dynamic-button-box" key={index + 1}>
                                                            {buttonItem ? parse(buttonItem) : ''}
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                            <Typography className="font-regular-text generative-transforming-subheading">
                                                {item.subheading ? parse(item.subheading) : ''}
                                            </Typography>
                                            {item.keyPoint && (
                                                <Box
                                                    display="flex"
                                                    gap={3}
                                                    alignItems={{xs: 'flex-start'}}
                                                    py={2}
                                                    flexDirection={{md: 'row', xs: 'column'}}
                                                >
                                                    <Box maxWidth={{sm: 200, xs: '100%'}}>
                                                        <Typography
                                                            className="custom-heading-gradient-color font-headers"
                                                            sx={keyPointSx}
                                                            fontSize={36}
                                                        >
                                                            {item.keyPoint[0].header}
                                                        </Typography>
                                                        <Typography className="font-regular-text">{item.keyPoint[0].body}</Typography>
                                                    </Box>
                                                    <Box
                                                        height={60}
                                                        border={0.5}
                                                        display={{md: 'block', xs: 'none'}}
                                                        alignSelf="center"
                                                    ></Box>
                                                    <Box maxWidth={{sm: 200, xs: '100%'}}>
                                                        <Typography
                                                            className="custom-heading-gradient-color font-headers"
                                                            sx={keyPointSx}
                                                            fontSize={36}
                                                        >
                                                            {item.keyPoint[1].header}
                                                        </Typography>
                                                        <Typography className="font-regular-text">{item.keyPoint[1].body}</Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                            {item?.outcomes && (
                                                <Box my={2.5}>
                                                    <Typography fontWeight="bold" fontSize={18} mb={2.5} className="font-headers">
                                                        Outcomes
                                                    </Typography>
                                                    {item.outcomes?.map((outcome: string, index: number) => (
                                                        <Box key={index} display="flex" alignItems="flex-start" gap={1} mb={1.5}>
                                                            <CheckCircleOutlineOutlinedIcon fontSize="small" />
                                                            <Typography>{outcome}</Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}

                                            <MUIButton
                                                onClick={item.redirect ? () => globalThis.open(item.redirect) : openForm}
                                                label={item.buttonText}
                                                variant="contained"
                                                size="medium"
                                                endIcon={<ArrowForwardIcon />}
                                                className="animated-button button-bg-primary moving-icon-button font-regular-text"
                                                sx={buttonSx}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item md={6}>
                                        <Box
                                            m={{md: 0, sm: 3, xs: 2}}
                                            borderRadius={4}
                                            overflow="hidden"
                                            border={item.boxShadow ? 6 : 0}
                                            borderColor={item.boxShadow}
                                            sx={item.boxShadow ? shadowSx : undefined}
                                        >
                                            <Box component={DisplayImage} alt={item.heading} src={item.imgSrc} sx={clientProjectImageSx} />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </AnimatedComponent>
                        </Box>
                    ))}
                </Stack>
            </MUIContainer>
        </Box>
    );
};

export default memo(ClientProject);
